import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import ListViewSettings from "../../components/ListViewSettings";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { getAllSalesOrder, printSalesOrder, printProformaInvoice } from "../../services/SalesOrderServices";
import { buildSalesOrderQueryParams } from "../../utils/listViewDateRanges";
import moment from 'moment';

function SalesOrderTable() {
    const InitialData = { records: [] };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const left_pinned = ['order_no', 'order_date'];
    const right_pinned = ['id'];

    const sumQty = (records, field) =>
        records.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const qtyTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.order_date === todayDateLabel
        );
        return {
            order_qty: sumQty(data.records, 'quantity'),
            scheduled_qty: sumQty(data.records, 'scheduled_qty'),
            supplied_qty: sumQty(data.records, 'supplied_qty'),
            balance_qty: sumQty(data.records, 'balance_qty'),
            today_order_qty: sumQty(todayRecords, 'quantity'),
        };
    }, [data.records, todayDateLabel]);

    const formatQty = (value) => Number(value || 0).toFixed(2);

    const todaySoCard = useMemo(() => ({
        label: 'Today SO',
        value: formatQty(qtyTotals.today_order_qty),
        gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #5EC8F0 100%)',
        shadow: '0 8px 20px rgba(0, 144, 216, 0.28)',
    }), [qtyTotals.today_order_qty]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Order Qty',
            value: formatQty(qtyTotals.order_qty),
            gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
        {
            label: 'Scheduled',
            value: formatQty(qtyTotals.scheduled_qty),
            gradient: 'linear-gradient(135deg, #305870 0%, #186098 55%, #5AA8D8 100%)',
            shadow: '0 8px 20px rgba(48, 88, 112, 0.24)',
        },
        {
            label: 'Supplied',
            value: formatQty(qtyTotals.supplied_qty),
            gradient: 'linear-gradient(135deg, #0E6A98 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
    ]), [qtyTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'order_no', headerName: 'SO No', size: 88 },
            { fieldName: 'order_date', headerName: 'SO Date', size: 86 },
            { fieldName: 'purchase_order_no', headerName: 'PO No', size: 78 },
            { fieldName: 'purchase_order_date', headerName: 'PO Date', size: 86 },
            { fieldName: 'salesrep_alias', headerName: 'CSM', size: 72 },
            { fieldName: 'customer', headerName: 'Customer', size: 120 },
            { fieldName: 'consignee', headerName: 'Site/Project', size: 120 },
            { fieldName: 'product', headerName: 'Grade', size: 75 },
            { fieldName: 'rate', headerName: 'Rate', size: 68, align: 'right' },
            { fieldName: 'quantity', headerName: 'Order Qty', size: 78, align: 'right' },
            { fieldName: 'scheduled_qty', headerName: 'Sch Qty', size: 72, align: 'right' },
            { fieldName: 'supplied_qty', headerName: 'Sup Qty', size: 72, align: 'right' },
            { fieldName: 'balance_qty', headerName: 'Bal Qty', size: 72, align: 'right' },
            { fieldName: 'delivery_mode', headerName: 'Delivery', size: 82 },
            { fieldName: 'is_approved', headerName: 'Approval', size: 88 },
            { fieldName: 'status', headerName: 'Status', size: 82 },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const mapSalesOrderRow = (salesorder) => ({
        id: salesorder.id,
        order_no: `${salesorder.prefix || ''}${salesorder.order_no || ''}`,
        order_date: salesorder.order_date,
        purchase_order_no: salesorder.purchase_order_no,
        purchase_order_date: salesorder.purchase_order_date,
        customer: salesorder.consignee?.customer?.name ?? '',
        salesrep_alias: salesorder.consignee?.customer?.salesrep?.alias
            || salesorder.consignee?.customer?.salesrep?.name
            || '',
        consignee: salesorder.consignee?.project_name ?? salesorder.consignee?.name ?? '',
        product: salesorder.product?.name ?? '',
        quantity: salesorder.quantity,
        rate: salesorder.rate,
        scheduled_qty: salesorder.scheduled_qty,
        supplied_qty: salesorder.supplied_qty,
        balance_qty: salesorder.balance_qty,
        delivery_mode: salesorder.delivery_mode,
        is_approved: salesorder.is_approved ? 'Approved' : 'Waiting',
        status: salesorder.status ? 'Active' : 'Inactive',
        is_batch_report_needed: 'Yes',
        hide_row_print: 'No',
    });

    const loadSalesOrders = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllSalesOrder(cookies, buildSalesOrderQueryParams(config))
            .then((salesorderList) => {
                const tableData = (salesorderList.sales_order_list || []).map(mapSalesOrderRow);
                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load sales order list');
                setData({ records: [] });
                setIsLoading(false);
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadSalesOrders(config);
    }, [loadSalesOrders]);

    const onEditSalesOrder = (salesOrderId) => {
        navigate(`/SalesOrderEdit/${salesOrderId}/`);
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS').map((c) => c.headerName),
            data.records.map((salesorder) => ({
                order_no: salesorder.order_no,
                order_date: salesorder.order_date,
                purchase_order_no: salesorder.purchase_order_no,
                purchase_order_date: salesorder.purchase_order_date,
                salesrep_alias: salesorder.salesrep_alias,
                customer: salesorder.customer,
                consignee: salesorder.consignee,
                product: salesorder.product,
                quantity: salesorder.quantity,
                rate: salesorder.rate,
                scheduled_qty: salesorder.scheduled_qty,
                supplied_qty: salesorder.supplied_qty,
                balance_qty: salesorder.balance_qty,
                delivery_mode: salesorder.delivery_mode,
                is_approved: salesorder.is_approved,
                status: salesorder.status,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Sales Order',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records,
            'salesorder.pdf',
        );
    };

    const onPrintSalesOrder = (salesOrderId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printSalesOrder(cookies, salesOrderId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Sales Order',
                    downloadFileName: 'salesorder.pdf',
                });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Print Failed');
                setIsLoading(false);
            });
    };

    const onPrintproformaInvoice = (deliveryId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printProformaInvoice(cookies, deliveryId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Proforma Invoice',
                    downloadFileName: 'proforma_invoice.pdf',
                });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Proforma print failed');
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getListViewSettings(cookies, 'sales_order')
            .then((response) => {
                loadSalesOrders(response.config || {});
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadSalesOrders, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/SalesOrder"
                    addNewLabel="New Sales Order"
                    homeLink="/Home"
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                />
                <div className="container">
                    <PremiumPageHeader
                        module="MARKETING"
                        title="List of Sales Order"
                        subtitle="Configure filters using the settings icon. The table loads based on your saved date range preferences."
                        modeLabel="VIEW MODE"
                        headerActions={
                            <ListViewSettings
                                viewKey="sales_order"
                                onSaved={handleSettingsSaved}
                            />
                        }
                    />
                </div>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        mb: 2.5,
                        py: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: todaySoCard.gradient,
                                boxShadow: todaySoCard.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                {todaySoCard.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                {todaySoCard.value}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.25,
                            flex: 1,
                            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        {summaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    flex: '0 0 auto',
                                    minWidth: 128,
                                    borderRadius: '12px',
                                    background: card.gradient,
                                    boxShadow: card.shadow,
                                    px: 1.75,
                                    py: 1.1,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF SALES ORDER"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditSalesOrder}
                            onRowPrint={onPrintSalesOrder}
                            hidePrint={false}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete
                            onRowBrPrint={onPrintproformaInvoice}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactRows
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SalesOrderTable;
